import { EventBusy } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Divider, IconButton, lighten, Stack, Typography, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AppointmentDetail from 'FRONTEND/components/Calendar/AppointmentList/AppointmentDetail';
import { CANCEL_REASON, DELETE_REASON } from 'FRONTEND/components/Calendar/CalendarPopover/closeReasons';
import ChargeAppointmentButton from 'FRONTEND/components/Calendar/ChargeAppointmentButton';
import ModalConfirmation from 'FRONTEND/components/Calendar/ModalConfirmation';
import {
  APPOINTMENT_DETAIL_ICONS,
  APPOINTMENT_TYPE_ICONS,
  getAppointmentTypeTranslationMap,
} from 'FRONTEND/components/Calendar/utils';
import VideoConfButton from 'FRONTEND/components/Calendar/VideoConfButton';
import InitialsAvatar from 'FRONTEND/components/InitialsAvatar';
import { cancelRequest as cancelAppointmentRequest, updateRequest as updateAppointmentRequest } from 'FRONTEND/reducers/appointmentSlice';
import { selectManagedExpertById } from 'FRONTEND/reducers/expertSlice';
import { RootState } from 'FRONTEND/reducers/types';
import { selectHasRight } from 'FRONTEND/reducers/userSlice';
import { FormattedAppointment } from 'FRONTEND/utils/backendApi/Appointments/appointment.types';
import { getAppointmentExpertNames } from 'FRONTEND/utils/backendApi/Appointments/appointmentUtils';
import { Expert } from 'FRONTEND/utils/backendApi/expert/expert.types';
import {
  APPOINTMENT_STATUS_AWAITING_CAPTURE,
  APPOINTMENT_STATUS_AWAITING_PAYMENT,
  APPOINTMENT_STATUS_PROPOSED,
  APPOINTMENT_STATUS_CANCELLED,
  OTHER,
  PARTICIPANT_STATUS_NEEDS_ACTION,
  PARTICIPANT_STATUS_TENTATIVE,
} from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';
import { getInitials } from 'FRONTEND/utils/initials';
import { RIGHTS } from 'FRONTEND/utils/rights';
import { PJAX_MAIN_CONTAINER_SELECTOR } from 'LEGACY/constants';

type AppointmentCardProps = {
  appointment: FormattedAppointment;
  embedType?: 'popup' | 'aside';
  rtcServerUrl?: string;
  isOngoingAppointment?: boolean;
  countdown?: string;
  displayMode?: 'panel' | 'default';
  onClose?: (reason: string) => void;
};

const getHeaderText = (appointment: FormattedAppointment, embedType: 'popup' | 'aside', isOngoingAppointment: boolean) => {
  if (embedType === 'popup') {
    return getAppointmentTypeTranslationMap()[appointment.appointmentType];
  }
  return isOngoingAppointment ? i18n._('An appointment is in progress') : i18n._('Next appointment');
};

const getAppointmentStatusInfo = (status: string): { text: string; color: string } | null => {
  switch (status) {
    case APPOINTMENT_STATUS_PROPOSED:
      return { text: i18n._('Pending appointment'), color: 'textSecondary' };
    case APPOINTMENT_STATUS_AWAITING_PAYMENT:
      return { text: i18n._('Awaiting payment'), color: 'warning.main' };
    case APPOINTMENT_STATUS_AWAITING_CAPTURE:
      return { text: i18n._('Awaiting capture'), color: 'success.main' };
    case APPOINTMENT_STATUS_CANCELLED:
      return { text: i18n._('Cancelled'), color: 'textSecondary' };
    default:
      return null;
  }
};

const AppointmentPreview = ({
  appointment,
  embedType = 'popup',
  rtcServerUrl,
  isOngoingAppointment,
  countdown,
  displayMode = 'default',
  onClose = () => {},
}: AppointmentCardProps) => {
  const dispatch = useDispatch();

  // User Management
  const userId = useSelector((state: RootState) => state.user.id);
  const isCurrentUserPatient = userId === appointment.patient?.userId;
  const calendarActorType = useSelector((state: RootState) => state.calendar.actorType);
  const managedUsers = useSelector((state: RootState) => state.expert.managedIdList); // Users who have delegated calendar to current user.
  const canCancelThroughDelegation = appointment.canCancelList?.some(id => managedUsers.includes(id)); // Check if current user has delegation over anyone that can cancel.
  const isAppointmentCancelable = calendarActorType !== 'resource' && (appointment?.canCancelList?.includes(userId) || canCancelThroughDelegation);
  const calendarExpert = useSelector((state: RootState) => selectManagedExpertById(state, appointment.expertId)) as Expert | undefined;
  const hasPatientListRight = useSelector((state: RootState) => selectHasRight(state, RIGHTS.PATIENTS_LIST));

  // Header Details
  const HeaderIcon = APPOINTMENT_TYPE_ICONS[appointment.appointmentType];
  const headerText = getHeaderText(appointment, embedType, isOngoingAppointment);
  const dateDetailText = countdown || `${dayjs(appointment.startDate).format('LL')}, ${dayjs(appointment.startDate).format('LT')} - ${dayjs(appointment.endDate).format('LT')}`;

  // Other Appointment Details
  // Refers to appointments created by PatientUsers from the Patient Portal.
  const isPatientRequestedAppointment = appointment.patient.isRequester;
  // `requiredMap` is null when the appointment has no required participant, and can be emptied by the secretary filter.
  const requiredList = Object.values(appointment.requiredMap || {});
  const avatarInitialsSource = isPatientRequestedAppointment ? requiredList[0]?.display : calendarExpert?.alias;
  // The patient record lives outside the right panel, so only offer the link from the calendar itself.
  const canOpenPatientRecord = displayMode === 'default' && hasPatientListRight && Boolean(appointment.patient?.reference);

  // Appointment Status Management
  const isCharged = useMemo(() => {
    // if appointment has consultation code, that means a fee was defined for it.
    return Boolean(appointment.extension.find(ext => ext.id === 'ConsultationCode'));
  }, [appointment]);
  const isAwaitingCaptureWithoutCharge = appointment.status === APPOINTMENT_STATUS_AWAITING_CAPTURE && !isCharged;

  const statusInfo = getAppointmentStatusInfo(appointment.status);

  // Action Buttons
  const needsAction = [PARTICIPANT_STATUS_NEEDS_ACTION, PARTICIPANT_STATUS_TENTATIVE].includes(appointment.requiredMap?.[appointment.expertId]?.status);
  const AcceptIcon = APPOINTMENT_DETAIL_ICONS.accept;
  const DeclineIcon = APPOINTMENT_DETAIL_ICONS.decline;

  const handlePatientRecordClick = () => {
    $.pjax({ url: `${appointment.patient.reference}history/`, container: PJAX_MAIN_CONTAINER_SELECTOR });
  };

  const handleAcceptAppointmentClick = () => {
    dispatch(updateAppointmentRequest({
      originalAppointment: appointment,
      acceptedIdList: [userId],
    }));
  };

  const handleDeclineAppointmentClick = () => {
    dispatch(updateAppointmentRequest({
      originalAppointment: appointment,
      declinedIdList: [userId],
    }));
  };

  // Cancel Appointment Modal
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const handleModalClose = (response: boolean) => {
    if (response) {
      dispatch(cancelAppointmentRequest({ appointmentId: appointment.appointmentId }));
      onClose(DELETE_REASON);
    }
    setIsCancelModalOpen(false);
  };

  const handleCancelClick = () => {
    setIsCancelModalOpen(true);
  };

  return (
    <Stack
      sx={{
        m: embedType === 'popup' ? 0 : 1,
        maxWidth: embedType === 'popup' ? 550 : 'none',
        border: embedType === 'popup' ? '' : (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: embedType === 'popup' ? 0 : '32px',
        overflow: 'hidden',
        backgroundColor: embedType === 'popup' ? 'transparent' : (theme) => lighten(theme.palette.primary.main, 0.9),
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        width='100%'
        sx={{ pt: 2, px: 2 }}
      >
        {/* Spacer for the close button */}
        {embedType === 'popup' && <Box sx={{ width: 40 }} />}
        <Stack direction='row' alignItems='center' width='100%' justifyContent='center'>
          {HeaderIcon && <HeaderIcon sx={{ mr: 1 }} />}
          <Typography
            variant='h5'
            sx={{
              fontWeight: 'bold',
              textAlign: 'center',
              textWrap: 'wrap',
              fontSize: '1rem',
            }}
          >
            {headerText}
          </Typography>
        </Stack>
        {embedType === 'popup' && (
          <Box sx={{ width: 40 }}>
            <IconButton aria-label='close' onClick={() => onClose(CANCEL_REASON)}>
              <CloseIcon />
            </IconButton>
          </Box>
        )}
      </Stack>
      <Stack minWidth={300} spacing={1} sx={{ p: 2 }}>
        <AppointmentDetail text={getAppointmentExpertNames(appointment)}>
          <InitialsAvatar
            picture={isPatientRequestedAppointment ? '' : calendarExpert?.picture}
            initials={getInitials(avatarInitialsSource)}
            sideSize={40}
            additionalUsers={isPatientRequestedAppointment ? undefined : appointment?.requiredMap}
            sx={{ border: (theme) => `2px solid ${theme.palette.background.paper}` }}
          />
        </AppointmentDetail>
        {appointment.startDate && <AppointmentDetail StartIcon={APPOINTMENT_DETAIL_ICONS.calendar} text={dateDetailText} />}
        {appointment.location && <AppointmentDetail StartIcon={APPOINTMENT_DETAIL_ICONS.location} text={appointment.location?.display} />}
        {appointment.patient && displayMode === 'default' && (
          <AppointmentDetail
            StartIcon={APPOINTMENT_DETAIL_ICONS.person}
            text={appointment.patient?.display}
            onClick={canOpenPatientRecord ? handlePatientRecordClick : undefined}
          />
        )}
        {appointment.reason && <AppointmentDetail StartIcon={APPOINTMENT_DETAIL_ICONS.healing} text={appointment.reason} />}
        {/* Appointment Status */}
        {statusInfo && (
          <Typography variant='subtitle2' color={statusInfo.color} component='p'>
            {statusInfo.text}
          </Typography>
        )}
        {(isAwaitingCaptureWithoutCharge && !isCurrentUserPatient) && <ChargeAppointmentButton appointment={appointment} />}
      </Stack>
      <Stack p='12px' gap={1} direction={embedType === 'popup' ? 'row' : 'column'} justifyContent={embedType === 'popup' ? 'flex-start' : 'center'}>
        {isAppointmentCancelable && <Button variant='outlined' onClick={handleCancelClick}>{i18n._('Cancel appointment')}</Button>}
        {appointment.status !== APPOINTMENT_STATUS_CANCELLED && appointment.appointmentType !== OTHER && (
          <VideoConfButton
            rtcServerUrl={rtcServerUrl}
            fullButton
            hideIcon
            color={appointment.expertId === userId ? 'primary' : 'inherit'}
            appointment={appointment}
          />
        )}
      </Stack>
      {/* Extra Actions (Accept/Refuse) */}
      {needsAction && (
        <>
          <Divider />
          {embedType === 'aside' ? (
            <Stack direction='row' spacing={1} justifyContent='center'>
              <Tooltip title={i18n._('Accept')}>
                <IconButton sx={{ color: 'success.main' }} onClick={handleAcceptAppointmentClick} size='large'>
                  <AcceptIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={i18n._('Decline')}>
                <IconButton sx={{ color: 'error.main' }} onClick={handleDeclineAppointmentClick} size='large'>
                  <DeclineIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : (
            <Stack>
              <Button sx={{ color: 'success.main' }} onClick={handleAcceptAppointmentClick}>
                {i18n._('Accept')}
              </Button>
              <Button sx={{ color: 'error.main' }} onClick={handleDeclineAppointmentClick}>
                {i18n._('Decline')}
              </Button>
            </Stack>
          )}
        </>
      )}
      <ModalConfirmation
        open={isCancelModalOpen}
        onClose={handleModalClose}
        message={i18n._('The cancellation of an appointment is final. Are you sure you want to cancel this appointment?')}
        title={<Stack direction='row' alignItems='center' gap={1}><EventBusy /> {i18n._('Confirm cancellation')}</Stack>}
      />
    </Stack>
  );
};

export default AppointmentPreview;
